import { useMutation, useQueryClient } from '@tanstack/react-query';
import { installmentsQueryKey } from './keys';
import { normalizeInstallment, sortInstallments } from '../types';
import type { InstallmentDto, InstallmentsHttpRequest, RawInstallmentDto } from '../types';

export interface UseRegenerateInstallmentsParams {
  documentId: string;
  /** Endpoint POST de regeneración (`/{id}/installments/regenerate`). */
  regenerateEndpoint: string;
  httpRequest: InstallmentsHttpRequest;
}

/**
 * Regenera el plan completo (`POST /{id}/installments/regenerate`), descartando
 * todos los ajustes manuales.
 *
 * - `onSuccess`: siembra el cache con la lista regenerada (todas `isManual=false`).
 * - `onSettled`: invalida `['document-installments', documentId]`.
 *
 * El backend devuelve 422 (`INSTALLMENT_COUNT_EXCEEDED` > 36) — el panel lo surfacea.
 */
export function useRegenerateInstallments({
  documentId,
  regenerateEndpoint,
  httpRequest,
}: UseRegenerateInstallmentsParams) {
  const queryClient = useQueryClient();
  const key = installmentsQueryKey(documentId);

  return useMutation<InstallmentDto[], Error, void>({
    mutationFn: async () => {
      const raw = await httpRequest<RawInstallmentDto[]>('POST', regenerateEndpoint, {});
      return sortInstallments((raw ?? []).map(normalizeInstallment));
    },
    onSuccess: (regenerated) => {
      queryClient.setQueryData<InstallmentDto[]>(key, regenerated);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
